import React, {useEffect, useState} from 'react';
import { motion } from 'framer-motion';
import './HealthChecker.css';
import {useSubmitHealthCheckerQuizMutation} from "api/healthCheckerApi";
import HealthScoreModal from "./HealthScoreModalExample";
import {HEALTH_QUESTIONS} from './Constants/HealthCheckerConstant'
import Swal from 'sweetalert2';


// Question Item Component
const QuestionItem = ({ question, isSelected, onToggle }) => {
    return (
        <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`wellness-question-item ${isSelected ? 'wellness-question-selected' : 'wellness-question-default'}`}
            onClick={onToggle}
        >
            <div className="wellness-question-content">
                <div className={`wellness-checkbox ${isSelected ? 'wellness-checkbox-selected' : 'wellness-checkbox-default'}`}>
                    {isSelected && (
                        <svg className="wellness-check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                    )}
                </div>
                <span className="wellness-emoji">{question.emoji}</span>
                <div className="wellness-question-text">
                    <p className="wellness-question-main">{question.question}</p>
                    <p className="wellness-question-hindi">{question.hindiQuestion}</p>
                </div>
            </div>
        </motion.div>
    );
};

// User Details Form Component
const UserDetailsForm = ({ formData, onChange, onSubmit, isSubmitting }) => {
    const [isFormValid, setIsFormValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        // Check if all fields are filled and mobile is valid
        const isValid =
            formData.name.trim() !== "" &&
            formData.mobile.trim().length >= 10 &&
            // formData.email.trim() !== "" &&
            // /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
            formData.address.trim() !== "";

        setIsFormValid(isValid);
    }, [formData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        onChange({ ...formData, [name]: value });
        setErrorMessage(""); // clear error on input
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();

        console.log('handleSubmit')
        // Check validation again on submit
        if (!isFormValid) {
            setErrorMessage("Please fill in all required fields correctly.");
            return;
        }
        onSubmit();
    };

    return (
        <div className="wellness-form-container">
            <h3 className="wellness-form-title">
                Your Details / आपकी जानकारी
            </h3>

            <div className="wellness-form-fields">
                <div className="wellness-form-group">
                    <label className="wellness-form-label">
                        Name / नाम *
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter your name / अपना नाम दर्ज करें"
                        className="wellness-form-input"
                        required
                    />
                </div>

                <div className="wellness-form-group">
                    <label className="wellness-form-label">
                        Mobile Number / मोबाइल नंबर *
                    </label>
                    <input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        placeholder="Enter your mobile number / अपना मोबाइल नंबर दर्ज करें"
                        className="wellness-form-input"
                        required
                        onInput={(e) =>
                            (e.target.value = e.target.value.replace(/[^0-9+\s]/g, ""))
                        }
                    />
                </div>

                {/* <div className="wellness-form-group">
                    <label className="wellness-form-label">
                        Email / ईमेल *
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email / अपना ईमेल दर्ज करें"
                        className="wellness-form-input"
                        required
                    />
                </div> */}

                <div className="wellness-form-group">
                    <label className="wellness-form-label">
                        Address / पता *
                    </label>
                    <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Enter your address / अपना पता दर्ज करें"
                        className="wellness-form-textarea"
                        rows="3"
                        required
                    />
                </div>

                {errorMessage && (
                    <p className="wellness-error-message" style={{ color: "red" }}>
                        {errorMessage}
                    </p>
                )}

                <motion.button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`wellness-submit-btn ${
                        !isFormValid || isSubmitting ? "wellness-submit-disabled" : ""
                    }`}
                >
                    {isSubmitting ? "Submitting..." : "Submit / जमा करें"}
                </motion.button>
            </div>
        </div>
    );
};

const formInitialState = {
    name: '',
    mobile: '',
    // email: '',
    address: ''
}
// Main Wellness Check Component
const WellnessCheckBooth = () => {
    const [selectedQuestions, setSelectedQuestions] = useState(new Set());
    const [formData, setFormData] = useState(formInitialState);
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [healthScore, setHealthScore] = useState();
    const [submitQuiz, { data: healthScoreResponse, isLoading: isScoreCalculating, error }] = useSubmitHealthCheckerQuizMutation();

    const onHide = () => {
        setShowScoreModal(false)
        setHealthScore(null)
    }

    useEffect(() => {
        if (error) {
            const message =
                error?.data?.message ||
                error?.error ||
                'Failed to submit health checker. Please try again.';
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: typeof message === 'string' ? message : 'Failed to submit health checker.',
            });
            return;
        }

        if (healthScoreResponse) {
            setShowScoreModal(true);
            setHealthScore(healthScoreResponse.marks);
            resetForm();
        }
    }, [healthScoreResponse, error]);

    const toggleQuestion = (questionId) => {
        const newSelected = new Set(selectedQuestions);
        if (newSelected.has(questionId)) {
            newSelected.delete(questionId);
        } else {
            newSelected.add(questionId);
        }
        setSelectedQuestions(newSelected);
    };

    const resetForm = () => {
        setSelectedQuestions(new Set());
        setFormData(formInitialState);
    }

    const handleSubmit = async () => {
        const submissionData = {
            full_name: formData.name,
            address: formData.address || null,
            mobile_number: formData.mobile || null,
            ...HEALTH_QUESTIONS.reduce((acc, q) => {
                acc[`q${q.id}`] = selectedQuestions.has(q.id);
                return acc;
            }, {}),
        };

        try {
            await submitQuiz(submissionData).unwrap();
        } catch (err) {
            // error UI is handled by the useEffect above
            console.error('Health checker submit failed:', err);
        }
    };

    return (
        <div className="wellness-container">
            <div className="wellness-content">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-4"
                >
                    <h1 className="wellness-main-title">
                        <span>🌿</span>
                        2-Minute Health Check
                        <span>🌿</span>
                    </h1>
                    <p className="wellness-subtitle">
                        Tick ✅ what matches you / जिसमें आप सहमत हैं उसे टिक करें
                    </p>
                </motion.div>

                {/* Questions Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="wellness-questions-section"
                >
                    <h2 className="wellness-section-title text-center">
                        Health Questions / स्वास्थ्य प्रश्न
                    </h2>

                    <div className="wellness-questions-list">
                        {HEALTH_QUESTIONS.map((question, index) => (
                            <motion.div
                                key={question.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 * index }}
                            >
                                <QuestionItem
                                    question={question}
                                    isSelected={selectedQuestions.has(question.id)}
                                    onToggle={() => toggleQuestion(question.id)}
                                />
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* User Details Form */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <UserDetailsForm
                        formData={formData}
                        onChange={setFormData}
                        onSubmit={handleSubmit}
                        isSubmitting={isScoreCalculating}
                    />
                </motion.div>
            </div>
            <HealthScoreModal
                score={healthScore}
                show={showScoreModal}
                onHide={onHide}
            />
        </div>
    );
};

export default WellnessCheckBooth;